// Spec 078 — constantes de domínio da cena 3D, compartilhadas por API e web.
// Uma verdade só: o service deriva a colocação e a vazão com estes números, e a UI mostra
// "janela de 10 min" / "limite de 100 MB" a partir dos mesmos valores.

/** Janela deslizante da vazão instantânea (FR-023). */
export const FLOW_WINDOW_MS = 10 * 60_000;
/** Mínimo de amostras na janela para a taxa não ser ruído (FR-023). */
export const FLOW_MIN_SAMPLES = 2;
/** Amostra com carimbo além disso é relógio adiantado — descartada, nunca vazão negativa. */
export const FLOW_FUTURE_TOLERANCE_MS = 60_000;
/**
 * Alcance da busca no ramo de SENSOR (`reading`). Curto de propósito: é a fonte a 1 Hz, e era ela
 * que fazia a cena varrer 60 min por consulta (20 máquinas × 1 Hz = 72 000 linhas) para alimentar
 * um cálculo de 10 min.
 *
 * Consequência aritmética, não regra nova: como 5 min < FLOW_WINDOW_MS, tudo que a busca traz já
 * cabe na janela viva ⇒ série de sensor é sempre `measured` ou `insufficient`, nunca `aged`.
 */
export const FLOW_SENSOR_LOOKBACK_MS = 5 * 60_000;
/**
 * Alcance no ramo MANUAL/IMPORT (`parameter_entry`) — o de antes, preservado. É lançamento humano:
 * esparso por natureza e barato de buscar. Cortá-lo para 5 min faria todo contador lançado à mão
 * nunca juntar as 2 amostras mínimas e virar "sem dado" permanente.
 */
export const FLOW_MANUAL_LOOKBACK_MS = 60 * 60_000;
/**
 * Teto de leituras POR SENSOR por consulta. 2× o esperado de um sensor a 1 Hz em 5 min (300):
 * folga para jitter de relógio e rajada de reenvio, corte para dispositivo em frequência anômala.
 *
 * Por sensor, nunca global: um teto global com ordenação decrescente faria um dispositivo tagarela
 * consumir o orçamento inteiro e apagar a série dos vizinhos.
 */
export const FLOW_MAX_SAMPLES_PER_SENSOR = 600;

/** Pixels do canvas 2D → metros da cena (um nó de 200 px ≈ 5 m). Ver research R8. */
export const SCENE_UNITS_PER_PX = 0.025;
/** Espaçamento da fila de fallback quando a linha não tem layout 2D (FR-011). */
export const SCENE_FALLBACK_SPACING = 4;

/**
 * Teto do arquivo `.glb` (FR-002). Subiu de 20 MB para 100 MB em 2026-09-03, a pedido: exports de
 * CAD reais passavam do teto antigo mesmo depois de otimizados.
 *
 * O limite não é gosto — é memória. O arquivo trafega inteiro em RAM no upload (buffer do
 * multipart), de novo na gravação (o driver do Postgres codifica `bytea` em hexadecimal, o que
 * dobra), e de novo a cada leitura sem cache. Antes de subir daqui, meça: com uploads simultâneos
 * o pico é múltiplo deste número. Otimizar o `.glb` (gltf-transform) continua sendo o caminho
 * certo — 100 MB é a folga para o caso difícil, não o tamanho esperado.
 */
export const MODEL3D_MAX_BYTES = 100 * 1024 * 1024;

/** Polling da cena autenticada (o WS dá a imediatez; o polling dá o movimento da vazão). */
export const SCENE_POLL_MS = 5_000;
/** Polling da rota de display (sem WS; dentro dos 5 s da SC-002). */
export const DISPLAY_POLL_MS = 4_000;
/** Sem atualização por mais que isso ⇒ "Sem atualização desde HH:mm" (FR-029). */
export const SCENE_STALE_MS = 30_000;
/** Inatividade na rota ambiente antes da órbita lenta (FR-037). */
export const AMBIENT_IDLE_MS = 60_000;
